import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import type { PresenceRecord, SchuKoPresenceRecord } from '../../../PresenceRecord.js';
import { AdornisTodo, AdornisTodoStatus } from '@adornis/todo/db/AdornisTodo.js';
import { ANY_CONTEXT, GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { TokenType } from '@adornis/users/db/types.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { hashPassword } from '@adornis/users/server/password.js';
import { createToken, verifyToken } from '@adornis/users/server/token.js';
import { getUserFromContext } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { BaseError } from '@adornis/validation/errors/BaseError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { validateIban } from '@adornis/validation/functions/iban.js';
import type { ExamPreparationSubject } from '@chancenwerk/enums/enums.js';
import {
  type Bundesland,
  type ContactMethod,
  type Gender,
  KPFStandardMembershipFeeOptions,
  Mitgliedschaften,
  type PaymentMethod,
  Registermethode,
  type SchoolSubject,
  StudentType,
  type Weekday,
  type WithdrawalFrom,
  type WithdrawalReason,
  type WithdrawalType,
} from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import type { UpdateResult } from 'mongodb';
import { DurchfuehrungsTag, PRODUCT_BLACKLIST } from '../../../_cms/products/entities/Product.js';
import type { ProductLKK } from '../../../_cms/products/entities/ProductLKK.js';
import { Adresse } from '../../../adresse.js';
import { AttendanceOverviewResult } from '../../../attendance/entities/AttendanceOverviewResult.js';
import { CampusLernheft } from '../../../campus-learning-content/entities/CampusLernheft.js';
import { ContentResultEventlog } from '../../../eventlogs/ContentResultEventlog.js';
import { MailEventLog, MailEventLogType } from '../../../eventlogs/MailEventlog.js';
import {
  registerAtChancenCampus,
  registerAtChancenCampusWithToken,
  registerUSManualWelcomeMail,
  registrationConfirmation,
  registrationGraduationConfirmation,
  registrationInformationOSForAdmin,
  registrationInformationUSForAdmin,
  registrationInterestAcquisitionOS,
  registrationInterestOS,
  updateInformationOSForAdmin,
} from '../../../mailer/mailTemplates.js';
import { chancenWerkMailer } from '../../../mailer/server/mailer.js';
import { ChancenPermission, ContextPrefix } from '../../../permissions.js';
import {
  BUT_SCHOOL_YEAR_PREFIX,
  compareSchoolYearLabels,
  currentSchoolYearLabel,
  isCurrentSchoolYear,
} from '../../../school-year.js';
import { ChancenRole } from '../../../semantics/ChancenRole.js';
import { Role } from '../../../semantics/Role.js';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import { StudentParticipationRelationship } from '../../../semantics/StudentParticipationRelationship.js';
import { getKPFAccessibilityMapForPermissions } from '../../kpf/chancenPublishFilterUtils.js';
import { KPF } from '../../kpf/entities/KPF.js';
import { PARTNER_BLACKLIST, Partner } from '../../partner/entities/Partner.js';
import { School } from '../../partner/entities/School.js';
import { ChancenwerkUser, PERSON_BLACKLIST } from './ChancenwerkUser.js';

export enum StudentState {
  OFFICIAL = 'official',
  GUEST = 'guest',
  COMPLETE = 'complete',
  BLANKO = 'blanko',
}

export enum DocumentMedium {
  DIGITAL = 'digital',
  ANALOG = 'analog',
}

/**
 * @entity KontoInformation
 */
@Entity()
export class BankInformation extends AdornisEntity {
  public static override _class = 'BankInformation';

  @Field(() => String)
  public kontoinhaber!: string;

  // TODO Can be removed and replaced by the validation from base, id available
  // eslint-disable-next-line complexity
  @validate<BankInformation>(({ value, key, source, step, stepKey }) => {
    const iban = value as string;
    if (!iban) return;
    if (!(stepKey === 'but' || !step)) return;

    const ibanCorrect = validateIban(iban);
    if (!ibanCorrect)
      throw new ValidationError(`${value} ist keine gültige IBAN.`, {
        key,
        source,
        translationKey: 'errorIBANInvalid',
      });
  })
  @Field(() => String)
  public iban!: string;

  @Field(() => String)
  public bic!: string;

  @Field(() => String)
  public kreditinstitut!: string;

  @Field(() => Boolean)
  public ibanFalsch!: boolean;

  @Field(() => String)
  public vermerkeZahlungsverkehr!: string;

  @Field(() => String)
  public debitorennummer: Maybe<string>;

  @Field(() => DateTime)
  public mandatsdatum!: DateTime;

  @Field(() => String)
  public mandatsreferenz!: string;

  @Field(() => String)
  public zahlungsart!: PaymentMethod;

  @Field(() => DateTime)
  public zahlungAusgesetztAb!: DateTime;

  @Field(() => DateTime)
  public zahlungAusgesetztBis!: DateTime;

  @validate<BankInformation>(({ value, key, source, step, stepKey }) => {
    if (step && stepKey === 'but')
      if (value && !KPFStandardMembershipFeeOptions.includes(value as number))
        throw new ValidationError(`${value as number} ist kein gültiger Mitgliedsbeitrag.`, {
          key,
          source,
          translationKey: 'errorBeitragInvalid',
        });
  })
  @Field(() => Int, { default: v => v ?? 20 })
  public mitgliedsbeitrag: Maybe<number>;

  @Field(() => Boolean, { default: v => v ?? false })
  public membershipFeeLowerThan20?: boolean;

  @Field(() => Boolean, { default: v => v ?? false })
  public wishesProofOfPayment: Maybe<boolean>;
}

/**
 * @entity BuT
 */
@Entity()
export class BuT extends AdornisEntity {
  public static override _class = 'BuT';

  public static override resolveID(): string {
    return A.getGloballyUniqueID();
  }

  @Field(() => String, { default: v => v || currentSchoolYearLabel(BUT_SCHOOL_YEAR_PREFIX) })
  public year!: string;

  public get isActiveYear() {
    return isCurrentSchoolYear(this.year);
  }

  /**
   * A BuT for the given school year carrying over everything that stays the same from one year to the next,
   * including `BuTBerechtigung`: a new-year entry only ever gets created because the student is presumed
   * still entitled, so the flag is copied like every other field here. If that assumption is wrong for a
   * given student, the case worker corrects it on the spot -- that's cheaper than requiring every entry to
   * be manually re-confirmed first.
   */
  public copyToSchoolYear(yearLabel: string): BuT {
    return new BuT({
      year: yearLabel,
      aktenzeichen: this.aktenzeichen,
      BuTBerechtigung: this.BuTBerechtigung,
      BuTBerechtigungDurch: this.BuTBerechtigungDurch,
      leistungstraeger: this.leistungstraeger,
      antragsteller: this.antragsteller,
    });
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public BuTBerechtigung: Maybe<boolean>;

  @Field(() => String)
  public assignedPersonForParentalContact: Maybe<string>;

  @Field(() => DateTime)
  public BuTUnterlagenEltern: Maybe<DateTime>;

  @Field(() => DateTime)
  public grundantragErhalten: Maybe<DateTime>;

  @Field(() => String)
  public grundantragMedium: Maybe<DocumentMedium>;

  @Field(() => DateTime)
  public leistungsbescheidErhalten: Maybe<DateTime>;

  @Field(() => String)
  public leistungsbescheidMedium: Maybe<DocumentMedium>;

  @Field(() => DateTime)
  public zeugnisErhalten: Maybe<DateTime>;

  @Field(() => String)
  public zeugnisMedium: Maybe<DocumentMedium>;

  @Field(() => DateTime)
  public verschwiegenheitsbogenErhalten: Maybe<DateTime>;

  @Field(() => String)
  public verschwiegenheitsbogenMedium: Maybe<DocumentMedium>;

  @Field(() => DateTime)
  public BuTUnterlagenVollstaendig: Maybe<DateTime>;

  /** potentiell DateTime */
  @Field(() => String)
  public elternkontakt: Maybe<string>;

  @Field(() => DateTime)
  public elternkontaktFehlgeschlagen: Maybe<DateTime>;

  @Field(() => String)
  public anlassKommentarElternkontakt: Maybe<string>;

  @Field(() => DateTime)
  public zusatzbogenSchuleVersendet: Maybe<DateTime>;

  @Field(() => DateTime)
  public zusatzbogenSchuleErhalten: Maybe<DateTime>;

  @Field(() => String)
  public zusatzbogenSchuleMedium: Maybe<DocumentMedium>;

  @Field(() => String)
  public vereinbarungsnotitzSchule: Maybe<string>;

  @Field(() => String)
  public leistungstraeger: Maybe<string>;

  @Field(() => DateTime)
  public antragTeilweiseVersendet: Maybe<DateTime>;

  @Field(() => DateTime)
  public antragVollstaendigVersendet: Maybe<DateTime>;

  @Field(() => String)
  public vereinbarungsnotitzAmt: Maybe<string>;

  @Field(() => String)
  public antragsteller: Maybe<string>;

  @Field(() => String)
  public aktenzeichen: Maybe<string>;

  @Field(() => String)
  public BuTBerechtigungDurch: Maybe<string>;

  @Field(() => DateTime)
  public BuTBewilligungAb: Maybe<DateTime>;

  @Field(() => DateTime)
  public BuTBewilligungBis: Maybe<DateTime>;

  @Field(() => String)
  public bewilligungMedium: Maybe<DocumentMedium>;

  @Field(() => DateTime)
  public abgerechnetBis: Maybe<DateTime>;

  @Field(() => String)
  public kommentarBuT: Maybe<string>;

  @Field(() => String)
  public kommentarAbrechnung: Maybe<string>;

  @Field(() => String)
  public anlassKommentarBuTSchuko: Maybe<string>;

  @Field(() => String)
  public kommentarWeiterbewilligung: Maybe<string>;

  @Field(() => DateTime)
  public kindNimmtNichtTeilStand: Maybe<DateTime>;

  @Field(() => [Fach], { default: v => v ?? [] })
  public faecher: Maybe<Fach[]>;

  @Field(() => String)
  public sonstigeFaecherTmp: Maybe<string>;

  @Field(() => DateTime)
  public BuTMassnahmenEinstellenBis: Maybe<DateTime>;

  @Field(() => DateTime)
  public leistungsbescheidBerlinPassGueltigBis: Maybe<DateTime>;
}

/** [Daniel-Todo] this is a field for BuT */
@Entity()
export class Fach extends AdornisEntity {
  public static override _class = 'Fach';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => String)
  public subject!: SchoolSubject;

  @Field(() => Boolean, { default: v => v ?? false })
  public approved!: boolean;

  @Field(() => Float)
  public berechneteStunden!: number;

  @Field(() => Float)
  public bewilligteStunden!: number;
}

/**
 * @entity Schulinformationen
 */
@Entity()
export class SchoolInformation extends AdornisEntity {
  public static override _class = 'Schulinformationen';

  constructor(doc: PartialEntityData<SchoolInformation>) {
    super(doc);
  }

  @Field(() => String)
  public klassenlehrer: Maybe<string>;

  @Field(() => Int)
  public jahrgangsstufe: Maybe<number>;

  @Field(() => String)
  public zug!: string;

  @Field(() => String)
  public abweichendeSchule?: string;

  @DataLoadedField(() => School, ({ abweichendeSchuleID }) => abweichendeSchuleID)
  public abweichendeSchuleResolved: Maybe<School>;

  @Field(() => String)
  public abweichendeSchuleID?: string;

  @Field(() => DateTime)
  public lastChangeOnJahrgangsstufe: Maybe<DateTime>;

  @Field(() => ID)
  public lastChangeOnJahrgangsstufeByUserID: Maybe<string>;

  @DataLoadedField(
    () => ChancenwerkUser,
    ({ lastChangeOnJahrgangsstufeByUserID }) => lastChangeOnJahrgangsstufeByUserID,
  )
  public lastChangeOnJahrgangsstufeByUser: Maybe<ChancenwerkUser>;

  /**
   * This resolver can use raw collection because we only resolve the user by name for internal purposes
   */
  @Dependencies<typeof Student>({
    classes: async () => [(await import('./ChancenwerkUser.js')).ChancenwerkUser],
    ids: async function (this: PartialEntityData<SchoolInformation>) {
      const classType = await import('./ChancenwerkUser.js');
      if (!this.lastChangeOnJahrgangsstufeByUserID) return [];

      const user = await loadMongoData(classType.ChancenwerkUser, this.lastChangeOnJahrgangsstufeByUserID);

      if (!user?._id) return [];

      return [user._id] as string[];
    },
  })
  @Field(() => String, {
    resolve: function (this: SchoolInformation) {
      return async () => {
        if (this.lastChangeOnJahrgangsstufeByUserName) return this.lastChangeOnJahrgangsstufeByUserName;
        if (!this.lastChangeOnJahrgangsstufeByUserID) return '?';
        const user = await loadMongoData(ChancenwerkUser, this.lastChangeOnJahrgangsstufeByUserID);
        if (!user) return '?';
        const userConstructed = constructValue(user) as ChancenwerkUser;
        return userConstructed.toString();
      };
    },
  })
  public lastChangeOnJahrgangsstufeByUserName: Maybe<string>;

  public get isConfirmed(): boolean {
    if (!this.lastChangeOnJahrgangsstufe) {
      return false;
    }

    const now = DateTime.now();
    // Use 1. of August as pivot point for the schoolyear
    // TODO ask Tobias what the real constraints are here
    return (
      (now.year === this.lastChangeOnJahrgangsstufe.year &&
        now.month < 8 &&
        this.lastChangeOnJahrgangsstufe.month < 8) ||
      (now.year === this.lastChangeOnJahrgangsstufe.year &&
        now.month >= 8 &&
        this.lastChangeOnJahrgangsstufe.month >= 8) ||
      (now.year - 1 === this.lastChangeOnJahrgangsstufe.year &&
        now.month < 8 &&
        this.lastChangeOnJahrgangsstufe.month >= 8)
    );
  }
}

/**
 * @entity Consents
 */
@Entity()
export class Consent extends AdornisEntity {
  public static override _class = 'Consent';

  @Field(() => DateTime)
  public gotSignedInfopostBack: Maybe<DateTime>;

  @Field(() => Boolean, { default: v => v ?? false })
  public einverstaendnisBild: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public einverstaendnisEntbindungVonVerschwiegenheit: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public confidential: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public learningDeficiencies: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? true })
  public einverstaendnisEvaluation: Maybe<boolean>;
}

/**
 * @entity Withdrawal
 */
@Entity()
export class Withdrawal extends AdornisEntity {
  public static override _class = 'Withdrawal';

  @Field(() => DateTime)
  public austrittsdatum: Maybe<DateTime>;

  @Field(() => Boolean, { default: v => v ?? false })
  public confirmationEmailSend: Maybe<boolean>;

  @Field(() => DateTime)
  public confirmationEmailSendAt: Maybe<DateTime>;

  @Field(() => Boolean, { default: v => v ?? false })
  public confirmationEmailProcessFinished: Maybe<boolean>;

  @Field(() => String)
  public kuendigungGrund: Maybe<WithdrawalReason>;

  @Field(() => String)
  public kuendigungGrundSonst: Maybe<string>;

  @Field(() => String)
  public kuendigungArt: Maybe<WithdrawalType>;

  @Field(() => String)
  public kuendigungDurch: Maybe<WithdrawalFrom>;
}

/**
 * @entity Funding
 */
@Entity()
export class Funding extends AdornisEntity {
  public static override _class = 'Funding';

  @Field(() => [String], { default: v => v ?? [] })
  public foerderbedarfLautEltern: Maybe<SchoolSubject[]>;

  @Field(() => [String], { default: v => v ?? [] })
  public teilleistungsschwaechen: Maybe<string[]>;

  @Field(() => String)
  public teilleistungsschwaechenSonst: Maybe<string>;

  @Field(() => String)
  public vermerkeSonst: Maybe<string>;
}

/**
 * @entity JoinInformation
 */
@Entity()
export class EntryInformation extends AdornisEntity {
  public static override _class = 'EntryInformation';

  constructor(doc: Partial<EntityData<EntryInformation>>) {
    super(doc);
  }

  @Field(() => DateTime)
  public eintrittsdatum: Maybe<DateTime>;

  @Field(() => String, { default: v => v ?? Registermethode.FORM })
  public registermethode: Maybe<Registermethode>;

  @Field(() => DateTime)
  public sendedPartTwoTillFive: Maybe<DateTime>;

  @Field(() => Boolean, { default: v => v ?? false })
  public campusRegisterDataSend!: boolean;
}

/**
 * @entity LegalGuardian
 */
@Entity()
export class LegalGuardian extends AdornisEntity {
  public static override _class = 'LegalGuardian';

  @Field(() => String)
  public vorname!: string;

  @Field(() => String)
  public nachname!: string;

  @Field(() => String)
  public staatsangehoerigkeit!: string;

  @Field(() => String)
  public spokenLanguage!: string;

  // This field will no longer be filled since CW_02_26 but is kept for backwards compatibility
  @Field(() => String)
  public beruf!: string;

  @Field(() => String)
  public handynummer!: string;

  @Field(() => String)
  public festnetznummer!: string;

  @Field(() => String)
  public email!: string;

  @Field(() => String)
  public geschlecht?: Gender;

  @Field(() => Adresse, { default: v => v ?? new Adresse({}) })
  public adresse!: Adresse;

  @Field(() => String)
  public bundesland: Maybe<Bundesland>;

  public override toString() {
    return `${this.vorname ?? ''} ${this.nachname ?? ''}`;
  }
}

/**
 * @entity LegalGuardian
 */
@Entity()
export class DayAvailable extends AdornisEntity {
  public static override _class = 'DayAvailable';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => String, { default: v => v ?? '' })
  public day!: string;

  @Field(() => [DateTime], { default: v => v ?? [] })
  public timesAvailable!: DateTime[];

  public static override resolveID(): string {
    return A.getGloballyUniqueID();
  }

  // We only need the time of a day so the timestamp for the date is set to the start of time
  // Comparison per string on time so we dont have to deal with the time on different days
  /**
   *
   * @param timeToSet the time of day to set
   * @returns true if the operations succeded, false if a guard option was triggered
   */
  public setTimeOfDayToAvailable(timeToSet: DateTime): boolean {
    if (this.timesAvailable.find(t => t.hour === timeToSet.hour && t.minute === timeToSet.minute)) return false;
    this.timesAvailable.push(
      DateTime.fromMillis(0, { zone: 'utc' }).plus({ hours: timeToSet.hour, minutes: timeToSet.minute }),
    );
    return true;
  }

  /**
   *
   * @param timeToSet the time of day to delete
   * @returns true if the operations succeded, false if a guard option was triggered
   */
  public removeTimeOfDayAvailable(timeToDelete: DateTime) {
    const indexToDelete = this.timesAvailable.findIndex(
      t => t.hour === timeToDelete.hour && t.minute === timeToDelete.minute,
    );
    if (indexToDelete === -1) return false;
    this.timesAvailable.splice(indexToDelete, 1);
    return true;
  }
}

export const GRADUATION_TYPES = [
  'Hauptschulabschluss',
  'Mittlerer Bildungsabschluss',
  'EF',
  'Abitur',
  'der Erste Schulabschluss (vormals Hauptschulabschluss)',
  'der Erweiterte Erste Schulabschluss (vormals Hauptschulabschluss nach Klasse 10) (ZAP)',
  'der Mittlere Schulabschluss (Fachoberschulreife) (ZAP)',
  'Der mittlere Bildungsabschluss',
  'Erster Bildungsabschluss',
  'Abitur auf dem Gymnasium',
  'Abitur auf der Stadtteilschule',
  'Berufsreife',
  'Qualifizierter Sekundarabschluss',
  'Mittlere Reife',
  'Fachhochschulreife',
];

@Entity()
export class GraduationInformation extends AdornisEntity {
  public static override _class = 'GraduationInformation';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => String)
  public type?: Maybe<string>;

  @Field(() => String)
  public firstSubjectGraduation?: Maybe<string>;

  @Field(() => String)
  public secondSubjectGraduation?: Maybe<string>;

  @Field(() => String)
  public thirdSubjectGraduation?: Maybe<string>;

  @Field(() => String)
  public openSubjectGraduation?: Maybe<string>;

  @Field(() => String)
  public graduationFirstTestTopic?: Maybe<string>;

  @Field(() => String)
  public graduationSecondTestTopic?: Maybe<string>;

  @Field(() => String)
  public graduationThirdTestTopic?: Maybe<string>;

  @Field(() => String)
  public firstSubjectGraduationCourseType?: Maybe<string>;

  @Field(() => String)
  public secondSubjectGraduationCourseType?: Maybe<string>;

  @Field(() => String)
  public thirdSubjectGraduationCourseType?: Maybe<string>;

  get subjects() {
    return [
      this.firstSubjectGraduation,
      this.secondSubjectGraduation,
      this.thirdSubjectGraduation,
      this.openSubjectGraduation,
    ].filter(s => !!s);
  }

  @Field(() => [DayAvailable], { default: v => v ?? [] })
  public daysAvailable!: DayAvailable[];

  hasSubject(subject: SchoolSubject) {
    return this.subjects.some(s => s?.startsWith(subject));
  }

  hasDayAvailable(day: Weekday, time: DateTime) {
    return this.daysAvailable.find(
      d => d.day === day && d.timesAvailable.find(t => t.hour === time.hour && t.minute === time.minute),
    );
  }

  public static override resolveID(): string {
    return A.getGloballyUniqueID();
  }
}

/**
 * @entity WeeklyTimeSlot
 */
@Entity()
export class ExamPreparationCopy extends AdornisEntity {
  public static override _class = 'ExamPreparationCopy';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => String)
  public weekday!: Weekday;

  @Field(() => DateTime)
  public startTime!: DateTime;

  @Field(() => DateTime)
  public endTime!: DateTime;

  @Field(() => String)
  public subject!: ExamPreparationSubject;

  @Field(() => ID)
  public originalID!: string;
}

/**
 * @entity ExamPreparationInformation
 */
@Entity()
export class ExamPreparationInformation extends AdornisEntity {
  public static override _class = 'ExamPreparationInformation';

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @Field(() => [ExamPreparationCopy], { default: v => v ?? [] })
  public courses!: ExamPreparationCopy[];
}

function _zug(zug: Maybe<string>) {
  if (!zug) return '';
  if (/^\d+$/.test(zug)) return `.${zug}`;
  return zug;
}

export enum StudentTags {
  FROM_OS_ACQUISITION = 'fromOSAcquisition',
  FROM_ABSCHLUSS = 'fromAbschluss',
}

/**
 * @entity Student
 */
@Entity()
export class Student extends ChancenwerkUser {
  public static override _class = 'Student';

  public static override readableName = 'Schüler:in';

  public static override get allFields() {
    return selectionSet(() => this, 10);
  }

  public get typeName() {
    let base = Student.readableName;

    if (this.hasTag(StudentTags.FROM_OS_ACQUISITION)) {
      return 'OS-Interessent:in';
    }

    if (this.hasTag(StudentTags.FROM_ABSCHLUSS)) {
      return 'ABS-Teilnehmer:in';
    }

    if (this.guestAccount) {
      base = 'Gast-' + base;
    }

    return base;
  }

  public get state(): StudentState {
    if (this.guestAccount) return StudentState.GUEST;
    if (this.blankoAccount) return StudentState.BLANKO;
    if (this.consent.gotSignedInfopostBack) return StudentState.OFFICIAL;
    return StudentState.COMPLETE;
  }

  public get schoolClass(): string {
    return `${this.schoolInformation.jahrgangsstufe ?? ''}${_zug(this.schoolInformation.zug)}`;
  }

  constructor(doc: Partial<EntityData<Student>>) {
    super(doc);
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public guestAccount: Maybe<boolean>;

  @Field(() => [String], { default: v => v ?? [] })
  public tags: Maybe<StudentTags[]>;

  public hasTag(tag: StudentTags) {
    return this.tags?.includes(tag);
  }

  public addTag(tag: StudentTags) {
    if (!this.tags) this.tags = [];
    this.tags.push(tag);
  }

  public removeTag(tag: StudentTags) {
    if (!this.tags) return;
    this.tags = this.tags.filter(t => t !== tag);
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public blankoAccount: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public notifyKickOff: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  public inactive: Maybe<boolean>;

  @Field(() => BankInformation, { default: v => v ?? new BankInformation({}) })
  public bankInformation!: BankInformation;

  @Field(() => [BuT], { default: v => v ?? [new BuT({})] })
  public buts!: BuT[];

  @Field(() => BuT, {
    resolve: function (this: Student) {
      return async (bqlFields: BaseQLSelectionSet<BuT>) => {
        const s = (await loadMongoData(Student, this._id, { projection: { buts: 1 } })) as Maybe<EntityData<Student>>;

        return s?.buts.find(b => b.isActiveYear) as Maybe<BuT>;
      };
    },
  })
  butActive: Maybe<BuT>;

  /** All BuTs oldest school year first. `buts` itself is stored in insertion order, which is not the same thing. */
  public get butsBySchoolYear(): BuT[] {
    return [...(this.buts ?? [])].sort((a, b) => compareSchoolYearLabels(a.year, b.year));
  }

  /** The BuT of the school year that is currently running, if it has been created already. */
  public get butOfCurrentSchoolYear(): Maybe<BuT> {
    return this.buts?.find(but => but.isActiveYear);
  }

  /**
   * The BuT to show in read only views: the running school year if it exists, otherwise the most recent one.
   * Never use `buts[0]` for this -- that is the OLDEST entry, not the current one.
   *
   * Do not use this for plausibility checks or reminders, those have to look at the running school year only
   * (see {@link butOfCurrentSchoolYear}) so that a finished school year cannot trigger them.
   */
  public get butToDisplay(): Maybe<BuT> {
    return this.butOfCurrentSchoolYear ?? this.butsBySchoolYear.at(-1);
  }

  @Field(() => SchoolInformation, { default: v => v ?? new SchoolInformation({}) })
  public schoolInformation!: SchoolInformation;

  @Dependencies<typeof Student>({
    classes: async () => [
      (await import('../../../semantics/StudentParticipationRelationship.js')).StudentParticipationRelationship,
      KPF,
    ],
    ids: async function (this: PartialEntityData<Student>) {
      const relationshipType = await import('../../../semantics/StudentParticipationRelationship.js');
      const relations = (await loadMongoData(relationshipType.StudentParticipationRelationship, {
        subjectID: this._id,
      })) as Maybe<StudentParticipationRelationship[]>;

      if (!relations?.length) return [];

      return [...relations.map(rel => rel._id), ...relations.map(rel => rel.objectID)] as string[];
    },
  })
  @Field(() => [KPF], {
    resolve: function (this: Student) {
      return async () => {
        if (this.kpfs) return this.kpfs;
        const relationshipType = await import('../../../semantics/StudentParticipationRelationship.js');
        const relations = (await loadMongoData(relationshipType.StudentParticipationRelationship, {
          subjectID: this._id,
        })) as Maybe<StudentParticipationRelationship[]>;
        return loadMongoData<typeof KPF>(KPF, { _id: { $in: relations?.map(r => r.objectID!) } }) as Promise<
          Maybe<KPF[]>
        >;
      };
    },
  })
  public kpfs: Maybe<KPF[]>;

  @Field(() => [ContentResultEventlog], {
    resolve: function (this: Student) {
      return async () => {
        return (await loadMongoData(
          ContentResultEventlog,
          { _class: ContentResultEventlog._class, userID: this._id },
          { sort: { createdAt: -1 } },
        )) as Maybe<ContentResultEventlog[]>;
      };
    },
  })
  public levumiResults: Maybe<ContentResultEventlog[]>;

  @Field(() => Boolean)
  public levumiHasMerged: Maybe<boolean>;

  /**
   * The attendance overview is resolved from the presence records, so the materialized view has to be
   * invalidated whenever one of them changes — otherwise a status corrected from PRESENT to EXCUSED
   * leaves a stale attendance behind, and the student export (which reads `adornisuser-materialized`)
   * keeps reporting an attendance for a day the student was excused. That is a billing-relevant error.
   *
   * `PresenceRecord` and `SchuKoPresenceRecord` share the `presence-record` collection but are
   * siblings, not sub/superclass — so each needs its own query; `loadMongoData` scopes by `_class`
   * and would otherwise silently drop the SchuKo half.
   */
  @Dependencies<typeof Student>({
    classes: async () => {
      const presenceRecordType = await import('../../../PresenceRecord.js');
      return [presenceRecordType.PresenceRecord, presenceRecordType.SchuKoPresenceRecord];
    },
    ids: async function (this: PartialEntityData<Student>) {
      if (!this._id) return [];
      const presenceRecordType = await import('../../../PresenceRecord.js');
      const [presenceRecords, schukoRecords] = await Promise.all([
        loadMongoData(
          presenceRecordType.PresenceRecord,
          {
            $or: [
              { studentRecordsUS: { $elemMatch: { userID: this._id } } },
              { studentRecordsOS: { $elemMatch: { userID: this._id } } },
              { lekoRecords: { $elemMatch: { userID: this._id } } },
            ],
          },
          { projection: { _id: 1 } },
        ) as Promise<Maybe<EntityData<PresenceRecord>[]>>,
        loadMongoData(
          presenceRecordType.SchuKoPresenceRecord,
          { schukoRecords: { $elemMatch: { userID: this._id } } },
          { projection: { _id: 1 } },
        ) as Promise<Maybe<EntityData<SchuKoPresenceRecord>[]>>,
      ]);

      return [...(presenceRecords ?? []), ...(schukoRecords ?? [])].map(record => record._id) as string[];
    },
  })
  @Field(() => AttendanceOverviewResult, {
    resolve: function (this: Student) {
      return async () => {
        const { AttendanceAPI } = await import('../../../attendance/api/AttendanceAPI.js');
        return AttendanceAPI.getOverview(this._id)({ attendanceCount: 1, lastAttendance: 1 });
      };
    },
  })
  public attendanceStats: Maybe<AttendanceOverviewResult>;

  @DataLoadedField(() => [CampusLernheft], ({ lernhefteWorkingOnIDs }) => lernhefteWorkingOnIDs ?? [])
  public lernhefteWorkingOn: Maybe<CampusLernheft[]>;

  @Field(() => [String])
  public lernhefteWorkingOnIDs: Maybe<string[]>;

  @DataLoadedField(() => [CampusLernheft], ({ lernhefteFinishedIDs }) => lernhefteFinishedIDs ?? [])
  public lernhefteFinished: Maybe<CampusLernheft[]>;

  @Field(() => [String])
  public lernhefteFinishedIDs: Maybe<string[]>;

  @Field(() => LegalGuardian, { default: v => v ?? new LegalGuardian({}) })
  public legalGuardian1!: LegalGuardian;

  @Field(() => LegalGuardian, { default: v => v ?? new LegalGuardian({}) })
  public legalGuardian2!: LegalGuardian;

  @Field(() => [String], { default: v => v ?? [] })
  public legalGuardianContactMethods?: ContactMethod[];

  @Field(() => String)
  public alternativeContactPerson: Maybe<string>;

  @Field(() => Consent, { default: v => v ?? new Consent({}) })
  public consent!: Consent;

  @Field(() => Withdrawal, { default: v => v ?? new Withdrawal({}) })
  public kuendigung!: Withdrawal;

  @Field(() => Funding, { default: v => v ?? new Funding({}) })
  public funding!: Funding;

  @Field(() => EntryInformation, { default: v => v ?? new EntryInformation({}) })
  public joinInformation!: EntryInformation;

  @Field(() => ID)
  public studentID: Maybe<string>;

  @validate<Student>(({ value, key, source, step }) => {
    if (!step)
      if (!value)
        throw new ValidationError('Bitte geben Sie den Schülertyp an.', {
          key,
          source,
          translationKey: 'errorStudentType',
        });
  })
  @Field(() => String)
  public schuelertyp!: StudentType;

  @validate<Student>(({ value, key, source, step, stepKey, target }) => {
    if (target.guestAccount) return;
    if (stepKey === 'personalData' || !step)
      if (value && value > DateTime.now().toMillis())
        throw new ValidationError(`Der Geburtstag darf nicht in der Zukunft liegen.`, {
          key,
          source,
          translationKey: 'errorStudentGeburtstag',
        });
  })
  @Field(() => DateTime)
  public override geburtstag: Maybe<DateTime>;

  @validate<Student>(({ value, key, source, step, stepKey, target }) => {
    if (target.guestAccount) return;
    if (step && stepKey === 'personalData' && target.schuelertyp === StudentType.OS)
      if (!value)
        throw new ValidationError(`Bitte gebe deine Handynummer ein.`, {
          key,
          source,
          translationKey: 'errorStudentHandynummer',
        });
  })
  @Field(() => String)
  public override handynummer: Maybe<string>;

  @Field(() => DateTime)
  public calledBackAt: Maybe<DateTime>;

  @Field(() => String)
  public staatsangehoerigkeit?: string;

  @Field(() => String)
  public spokenLanguage?: string;

  @validate<Student>(({ value, key, source, step, stepKey, target }) => {
    if (target.guestAccount) return;
    if (step && stepKey === 'personalData' && target.schuelertyp === StudentType.OS) {
      if (!value)
        throw new ValidationError(`Bitte gebe deine E-Mail Adresse ein.`, {
          key,
          source,
          translationKey: 'errorStudentEmail',
        });
      if (value && !emailRegexCheck(value as string))
        throw new ValidationError(`${value as string} ist keine gültige E-Mail Adresse.`, {
          key,
          source,
          translationKey: 'errorStudentInvalidEmail',
        });
    }
  })
  @Field(() => String)
  public override email!: string;

  @Field(() => String, { default: v => v ?? Mitgliedschaften.HILFE_GEBEN_NEHMEN })
  public mitgliedschaft?: Mitgliedschaften;

  public get active() {
    return !this.kuendigung?.austrittsdatum || this.kuendigung.austrittsdatum > DateTime.now();
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public editedByInternal!: boolean;

  @Field(() => String)
  public bildungsKarteNummer: Maybe<string>;

  @Field(() => String)
  public zustellbesonderheiten: Maybe<string>;

  @Field(() => [DurchfuehrungsTag], { default: v => v ?? [] })
  public lernfoerderungDuerchfuehrungsTagChoosen?: DurchfuehrungsTag[];

  // Offer 1
  @Field(() => [DurchfuehrungsTag], { default: v => v ?? [] })
  public intensiveCoursesChoosen?: DurchfuehrungsTag[];

  // Offer 2
  @Field(() => [DurchfuehrungsTag], { default: v => v ?? [] })
  public intensiveCoursesSingleChoosen?: DurchfuehrungsTag[];

  // Offer 3
  @Field(() => [String], { default: v => v ?? [] })
  public examPreperationChoosen?: string[];

  @Field(() => [DayAvailable], { default: v => v ?? [] })
  public daysAvailable!: DayAvailable[];

  @Field(() => String)
  public preferedFirstSubject?: Maybe<string>;

  @Field(() => String)
  public preferedSecondSubject?: Maybe<string>;

  @Field(() => String)
  public preferedThirdSubject?: Maybe<string>;

  @Field(() => String)
  public preferedOtherSubject?: Maybe<string>;

  @Field(() => GraduationInformation)
  public graduationInformation: Maybe<GraduationInformation>;

  @Field(() => ExamPreparationInformation)
  public examPreparationInformation: Maybe<ExamPreparationInformation>;

  static override async afterPasswordChangeWithoutToken(userID: string) {
    const user = await Student.getByID<Student>(userID)({ email: 1, joinInformation: { campusRegisterDataSend: 1 } });
    if (!user) {
      return;
    }

    // If the student has a dummy email we activate him for the campus
    if (/dummy-[0-9]+@chancenwerk\.de/.test(user.email) && !user?.joinInformation.campusRegisterDataSend) {
      const coll = await getRawCollection<ChancenwerkUser>(this._collectionName);
      await coll.updateOne({ _id: userID }, { $set: { 'joinInformation.campusRegisterDataSend': true } });
    }
  }

  static override async canChangePasswordWithoutToken(userID: string) {
    const caller = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
      roles: { permissions: 1, contexts: 1, name: 1 },
    });

    const user = await Student.getByID<Student>(userID)({ kpfs: { _id: 1 } });

    if (!caller?.hasPermission(ChancenPermission.raum.CHANGE_STUDENT_PASSWORD, ANY_CONTEXT)) {
      return false;
    }

    const accessible = (
      await getKPFAccessibilityMapForPermissions(
        [ChancenPermission.global.READ_STUDENT],
        caller as unknown as ChancenwerkUser,
      )
    ).get(ChancenPermission.global.READ_STUDENT);

    if (!accessible) {
      return false;
    }

    if (accessible.includes(GLOBAL_CONTEXT)) {
      return true;
    }

    return !!user?.kpfs?.some(k => {
      return k._id && accessible.includes(k._id);
    });
  }

  protected override async getComposedRegisterMail() {
    if (this.joinInformation.registermethode === Registermethode.TOKEN)
      return Message.compose({
        subject: 'Willkommen auf dem ChancenCampus',
        html: registerAtChancenCampusWithToken({
          username: this.username,
          confirmationUrl: (await Student.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
        }),
      });
    else
      return Message.compose({
        subject: 'Willkommen auf dem ChancenCampus',
        html: registerAtChancenCampus({
          username: this.username,
          confirmationUrl: (await ChancenwerkUser.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
        }),
      });
  }

  protected override async saveMailEventLog(error?: Error) {
    await new MailEventLog({
      userID: this._id,
      mailType:
        this.joinInformation.registermethode === Registermethode.TOKEN
          ? MailEventLogType.TOKEN_REGISTER
          : MailEventLogType.REGISTER,
      successful: !error,
      sendMailError: error?.message,
      emailAddress: this.email,
    }).create();
  }

  protected override async sendRegistrationMail() {
    await super.sendRegistrationMail();

    // ? We had a problem where a this.save() triggered a validation that should not be failing?
    // ! This is a quick workaround to just set the campusRegisterDataSend without going through
    // ! all the validations again.
    // TODO: investigate and fix
    const col = await getRawCollection<Student>(Student._class);
    await col.updateOne(
      { _class: Student._class, _id: this._id },
      { $set: { 'joinInformation.campusRegisterDataSend': true } },
    );
  }

  /**
   * @param weekdayToSet the day to set
   * @param timeOnDay the time of day to set
   * @returns true if the operations succeded, false if a guard option was triggered
   */
  public setTimeOnDayAvailable(weekdayToSet: Weekday, timeOnDay: DateTime) {
    let dayAvailable = this.daysAvailable.find(dayAvailable => dayAvailable.day === weekdayToSet);
    if (!dayAvailable) {
      dayAvailable = new DayAvailable({ day: weekdayToSet });
      this.daysAvailable.push(dayAvailable);
    }
    return dayAvailable.setTimeOfDayToAvailable(timeOnDay);
  }

  /**
   * @param weekdayToDelete the day to delete
   * @param timeOnDay the time of day to delete
   * @returns true if the operations succeded, false if a guard option was triggered
   */
  public removeTimeOnDayAvailable(weekdayToDelete: Weekday, timeOnDay: DateTime): boolean {
    const indexOfDayAvailable = this.daysAvailable.findIndex(dayAvailable => dayAvailable.day === weekdayToDelete);
    if (indexOfDayAvailable === -1) return false;
    const dayAvailable = this.daysAvailable[indexOfDayAvailable];
    if (!dayAvailable) return false;
    if (!dayAvailable.removeTimeOfDayAvailable(timeOnDay)) return false;
    if (dayAvailable.timesAvailable.length > 0) return true;
    this.daysAvailable.splice(indexOfDayAvailable, 1);
    return true;
  }

  /**
   *
   * @param weekday The Weekday for the time
   * @param timeOnDay The time on said weekday
   * @returns true, if the day/time got set. False if the day/time got unset
   */
  public toggleTimeOnDayAvailable(weekday: Weekday, timeOnDay: DateTime) {
    if (this.setTimeOnDayAvailable(weekday, timeOnDay)) return true;
    else if (this.removeTimeOnDayAvailable(weekday, timeOnDay)) return false;
  }

  public override async remove(): Promise<boolean> {
    const { removeStudentFromCampusUserGroups } = await import('../../../CampusUserGroup.js');
    await removeStudentFromCampusUserGroups(this._id);
    return super.remove();
  }

  public trimWhitespace() {
    const removeTrailingWhitespaces = (key: string, obj: any, depth: number) => {
      if (depth > 15) {
        return obj;
      }

      if (key === 'name' || key === '_class') {
        return obj;
      }

      if (typeof obj === 'string') {
        return obj.trimEnd();
      }
      if (typeof obj === 'object') {
        for (const key in obj) {
          obj[key] = removeTrailingWhitespaces(key, obj[key], depth + 1);
        }
      }
      return obj;
    };

    removeTrailingWhitespaces('', this, 0);
  }

  static override async transformBeforeUpdate(oldEntity: Student, update: Student): Promise<void> {
    if (
      update.has('schoolInformation') &&
      update.schoolInformation.jahrgangsstufe !== oldEntity.schoolInformation.jahrgangsstufe
    ) {
      if (context.serverContext || !context.userID) {
        return;
      }
      update.schoolInformation.lastChangeOnJahrgangsstufe = DateTime.now();
      update.schoolInformation.lastChangeOnJahrgangsstufeByUserID = context.userID;
    }
    await super.transformBeforeUpdate(oldEntity, update);
  }

  @Mutation(() => Boolean)
  public static setOwnPreferedSubjects(
    @Arg('firstSubject', () => String) firstSubject: string,
    @Arg('secondSubject', () => String) secondSubject: string,
    @Arg('thirdSubject', () => String) thirdSubject: string,
  ) {
    return async () => {
      const user = await getUserFromContext<Student>({
        _id: 1,
        preferedFirstSubject: 1,
        preferedSecondSubject: 1,
        preferedThirdSubject: 1,
      });
      if (!user || !user._id) {
        return false;
      }

      const col = await getRawCollection<Student>(Student._collectionName);
      const res = await col.updateOne(
        { _id: user._id },
        {
          $set: {
            preferedFirstSubject: firstSubject.length > 0 ? firstSubject : undefined,
            preferedSecondSubject: secondSubject.length > 0 ? secondSubject : undefined,
            preferedThirdSubject: thirdSubject.length > 0 ? thirdSubject : undefined,
          },
        },
      );

      return res.modifiedCount === 1;
    };
  }

  @Mutation(() => Boolean)
  public static isStudentAcronymFree(@Arg('acronym', () => String) acronym: string) {
    return async (gqlFields: void) => {
      const col = await getCollection(AdornisUser._class);
      const results = await col
        .find({ username: { $regex: `^${acronym.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } })
        .toArray();
      return !results.length;
    };
  }

  // TODO Refrator this into multiple functions
  // eslint-disable-next-line complexity
  public static async getUniqueAcronymFromStudent(student: Student) {
    // How many symbols of each name should be included under normal cirumstances
    const firstTryPrenameLength = 3;
    const firstTryLastnameLength = 3;

    // Build only with max lenght vor-nachname
    const prenameLenght = student.vorname?.length ?? 0;
    const lastnameLenght = student.nachname?.length ?? 0;
    let prename = student.vorname?.trim() ?? '';
    let lastname = student.nachname?.trim() ?? '';
    prename = prename.replaceAll(/[^\w]/gi, '');
    lastname = lastname.replaceAll(/[^\w]/gi, '');

    // Filter names
    prename = prename.replaceAll(/\u00e4/g, 'ae');
    prename = prename.replaceAll(/\u00c4/g, 'Ae');
    prename = prename.replaceAll(/\u00f6/g, 'oe');
    prename = prename.replaceAll(/\u00d6/g, 'Oe');
    prename = prename.replaceAll(/\u00fc/g, 'ue');
    prename = prename.replaceAll(/\u00dc/g, 'Ue');
    prename = prename.replaceAll(/\u00df/g, 'ss');

    lastname = lastname.replaceAll(/\u00e4/g, 'ae');
    lastname = lastname.replaceAll(/\u00c4/g, 'Ae');
    lastname = lastname.replaceAll(/\u00f6/g, 'oe');
    lastname = lastname.replaceAll(/\u00d6/g, 'Oe');
    lastname = lastname.replaceAll(/\u00fc/g, 'ue');
    lastname = lastname.replaceAll(/\u00dc/g, 'Ue');
    lastname = lastname.replaceAll(/\u00df/g, 'ss');
    let proposedAcronym = '';

    // Sometimes the students have very short names, in that case init with the max lenght of that name
    proposedAcronym =
      prename.substring(0, prenameLenght >= firstTryPrenameLength ? firstTryPrenameLength : prenameLenght) +
      '' +
      lastname.substring(0, lastnameLenght >= firstTryLastnameLength ? firstTryLastnameLength : lastnameLenght);

    // Save the inital, if we have to fallback to numbers;
    const firstProposal = proposedAcronym;

    // Build longer acronyms with the name
    let prenameIndex = (prenameLenght >= firstTryPrenameLength ? firstTryPrenameLength : prenameLenght) - 1;
    let lastnameIndex = (lastnameLenght >= firstTryLastnameLength ? firstTryLastnameLength : lastnameLenght) - 1;
    while (
      !(await Student.isStudentAcronymFree(proposedAcronym)()) &&
      prenameIndex < prenameLenght &&
      lastnameIndex < lastnameLenght
    ) {
      if (prenameIndex === lastnameIndex) {
        prenameIndex++;
        proposedAcronym = `${prename.substring(0, prenameIndex + 1)}${lastname.substring(0, lastnameIndex + 1)}`;
      } else if (prenameIndex > lastnameIndex) {
        prenameIndex--;
        lastnameIndex++;
        proposedAcronym = `${prename.substring(0, prenameIndex + 1)}${lastname.substring(0, lastnameIndex + 1)}`;
      } else if (prenameIndex < lastnameIndex) {
        prenameIndex++;
        proposedAcronym = `${prename.substring(0, prenameIndex + 1)}${lastname.substring(0, lastnameIndex + 1)}`;
      } else {
        logger.warn('Binary acronym counter failed. This should not happen! Falling back to numbers...');
      }
    }

    // The acronym generation didnt work for some reason (name to short, all acronyms unavailable, ...)
    // In that case we fall back on numbers
    if (!(await Student.isStudentAcronymFree(proposedAcronym)())) {
      let newNumber = 1;
      proposedAcronym = firstProposal.concat(newNumber.toString());
      while (!(await Student.isStudentAcronymFree(proposedAcronym)())) {
        proposedAcronym = firstProposal.concat(newNumber.toString());
        newNumber++;
      }
    }

    return proposedAcronym;
  }

  /**
   * This doesn't check for permission, since parents need to create students from registration form
   */
  @Mutation(() => Student)
  public static createStudentWithKPF(
    @Arg('student', () => Student) student: Student,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Student>) => {
      const created = await Student.createStudent(student)({
        _id: 1,
        roles: AdornisRoleToContextsWrapper.allFields,
        ...gqlFields,
      });

      const rawCol = await getRawCollection<EntityData<Student>>(Student._collectionName);
      await rawCol.updateOne(
        { _id: created._id },
        {
          $set: {
            roles: [new AdornisRoleToContextsWrapper({ name: Role.STUDENT, contexts: [`kpf_${kpfID}`] }).toJSON()],
          },
        },
      );

      new StudentParticipationRelationship({
        subjectID: created._id,
        predicateID: Role.STUDENT,
        objectID: kpfID,
      }).create();

      return created;
    };
  }

  @Mutation(bool => Boolean)
  public static sendStudentAcquisitionNotification(
    @Arg('student', () => Student) student: Student,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return async () => {
      if (student.hasTag(StudentTags.FROM_OS_ACQUISITION)) {
        const interestRecipients = await Student.resolveMailReferencesForInterest(kpfID);
        interestRecipients.internalRecipients.push(...(await Student.findEmailsForRole(kpfID, Role.SCHOOL_SUPPORT)));
        interestRecipients.internalRecipients.push(
          ...(await Student.findEmailsForRole(kpfID, Role.PROGRAM_MANAGEMENT)),
        );

        const partnerCol = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
        const partner = await partnerCol.findOne({ _id: interestRecipients.kpf.schoolID });

        const recipients = [...new Set(interestRecipients.internalRecipients.filter(r => !!r && r.length > 0))];

        if (!recipients.length) {
          logger.error(
            { event: 'acquisition_interest_mail_no_recipients', studentID: student._id, kpfID },
            'Keine Empfänger:innen für die Interessensbekundung gefunden, es wird keine Mail versandt',
          );
          return;
        }

        const message = await Message.compose({
          subject: 'Neue Interessensbekundung OS-Akquise',
          html: registrationInterestAcquisitionOS({
            studentPrename: student.vorname ?? '',
            studentLastname: student.nachname ?? '',
            studentEmail: student.email ?? '',
            studentPhone: student.handynummer ?? '',
            studentSchool: partner?.name ?? '',
          }),
        });

        // One message per recipient: sendMail() fans out with Promise.all and rethrows,
        // so a single stale address (e.g. a colleague who left) would otherwise stop
        // the mail for everyone else too.
        const { successfulMessages, erroredMessages } = await chancenWerkMailer.sendMails(
          recipients.map(r => ({ message, recipients: new Recipient(r) })),
        );

        for (const errored of erroredMessages) {
          logger.error(
            {
              event: 'acquisition_interest_mail_failed',
              err: errored.error,
              recipient: errored.recipients,
              studentID: student._id,
              kpfID,
            },
            'Interessensbekundung konnte an eine:n Empfänger:in nicht zugestellt werden',
          );
        }

        if (!successfulMessages.length) {
          logger.error(
            {
              event: 'acquisition_interest_mail_undeliverable',
              studentID: student._id,
              kpfID,
              recipients,
            },
            'Interessensbekundung konnte an KEINE:N der Empfänger:innen zugestellt werden',
          );
        }
      }
    };
  }

  /**
   * This doesn't check for permission, since parents need to create students from registration form
   */
  @Mutation(() => Student)
  public static createStudent(@Arg('student', () => Student) student: Student) {
    return async (gqlFields: BaseQLSelectionSet<Student>) => {
      const { ChancenwerkCounter } = await import('../../../counters.js');

      try {
        // get new incremental id
        const studentID = (await ChancenwerkCounter.getIncrementingStudentID()()).toString();

        // get new acronym for student
        const acronym = await Student.getUniqueAcronymFromStudent(student);

        logger.warn(`Creating new User with acronym ${acronym} and id ${studentID}`);
        if (
          !student.email ||
          /dummy-[0-9]+@chancenwerk\.de/.test(student.email) ||
          /new@chancenwerk.org/.test(student.email)
        ) {
          if (!!student.legalGuardian1.email && student.legalGuardian1.email !== ' ')
            student.email = student.legalGuardian1.email;
          else if (!!student.legalGuardian2.email && student.legalGuardian2.email !== ' ')
            student.email = student.legalGuardian2.email;
          else student.email = `dummy-${studentID as string}@chancenwerk.de`;
        }

        if (!student.joinInformation.eintrittsdatum) {
          student.joinInformation.eintrittsdatum = DateTime.now();
        }

        student._class = 'Student'; // Reset after validation
        student.studentID = studentID;
        student.roles = [new AdornisRoleToContextsWrapper({ name: Role.STUDENT, contexts: [GLOBAL_CONTEXT] })];
        student.username = acronym;

        const id = await Student.createNewUser(student, `Ab3!${(Math.random() + 1).toString(36).substring(7)}`);

        const coll = await getRawCollection<EntityData<Student>>(Student._collectionName);
        const newStudent = await coll.findOne({ _id: id });

        const constructedStudent = constructValue(newStudent) as Student;
        logger.warn(`id of new student ${constructedStudent._id}`);
        return constructedStudent;
      } catch (e: any) {
        logger.error({ e }, 'Fehler: Beim erstellen eines neuen Schülers');
        throw new Error(
          `Benutzer:in kann nicht angelegt werden. StudentID ist möglicherweise bereits vergeben ${e.message}`,
        );
      }
    };
  }

  @Mutation(() => String)
  public static createBlanko(
    @Arg('student', () => Student) student: Student,
    @Arg('password', () => String) password: string,
  ) {
    return async (gqlFields: void) => {
      try {
        student.email = `dummy-blanko-${Math.floor(Math.random() * 900000)}@chancenwerk.de`;
        student._class = 'Student';
        student.blankoAccount = true;
        student.roles = [new AdornisRoleToContextsWrapper({ name: Role.STUDENT, contexts: [GLOBAL_CONTEXT] })];
        await this.createNewUser(student, password, false);
      } catch (e: any) {
        logger.warn(e.message);
        throw new Error('Blanko Benutzer:in kann nicht angelegt werden.');
      }
    };
  }

  private static async createNewUser(userObj: AdornisUser, password: string, loginAfterCreate = false) {
    const collection = await getRawCollection<AdornisUser>(AdornisUser._collectionName);

    // Check if username is unique
    const user: Maybe<EntityData<AdornisUser>> = await collection.findOne(
      { username: { $regex: `^${userObj.username.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } },
      { projection: { password: 0 } },
    );
    if (user?._id)
      throw new BaseError('createNewUser(): Username is already in use', { translationKey: 'users.username_in_use' });
    const id = await AdornisUser.createEntity(userObj._class, userObj)();
    await collection.updateOne({ _id: id }, { $set: { password: hashPassword(password) } });
    if (!context.serverContext) {
      if (loginAfterCreate) {
        context.userID = id;
        context.userClass = userObj._class;
        context.impersonatingUser = undefined;
        context.impersonatingUserClass = undefined;
        await context.recreate();
      }
    }
    return id;
  }

  @Mutation(() => Boolean)
  public static createDebitorennummer(@Arg('student', () => Student) student: Student) {
    return async (gqlFields: void) => {
      try {
        await this.ensureDebitorennummer(student);
      } catch (e) {
        logger.error(e);
        return false;
      }
      return true;
    };
  }

  // eslint-disable-next-line complexity
  static async ensureDebitorennummer(entity: Student) {
    const { ChancenwerkCounter, CounterType } = await import('../../../counters.js');

    const relCol = await getCollection<StudentParticipationRelationship>(StudentParticipationRelationship._class);
    const kpfRelationsFromStudent = await relCol.find({ subjectID: entity._id }).toArray();
    const studentColl = await getRawCollection<Student>(Student._collectionName);
    const kpfColl = await getRawCollection<KPF>(KPF._collectionName);
    const student = constructValue(await studentColl.findOne({ _id: entity._id })) as Student;
    if (!student) {
      throw new Error('Given Student is not in Database');
    }

    if (
      student.schuelertyp === StudentType.OS ||
      !!student.bankInformation.debitorennummer ||
      !kpfRelationsFromStudent.length
    )
      return logger.info(
        {
          type: student.schuelertyp,
          existing: student.bankInformation.debitorennummer,
          kpfID: kpfRelationsFromStudent.length,
        },
        'es wird keine Debitorennummer angelegt',
      );

    // TODO we only use the first kpf. Student have one kpf 99% of the time. And probably more at the creation of the student
    const kpf = constructValue(
      await kpfColl.findOne({ _id: kpfRelationsFromStudent.at(0)?.objectID ?? '' }),
    ) as Maybe<KPF>;

    // SONDERREGELUNG NRW_DIG_LKK
    if (kpf && kpf.kuerzel === 'NRW_DIG_LKK') {
      student.bankInformation.debitorennummer = student.studentID;
      await studentColl.updateOne(
        { _id: student._id },
        { $set: { 'bankInformation.debitorennummer': student.bankInformation.debitorennummer } },
      );
      return;
    }
    const kostenstelle = kpf?.kostenstellennummer;
    if (!kpf || !kostenstelle)
      return logger.info(
        { kpf: !!kpf, kostenstelle },
        'es wird keine Debitorennummer angelegt weil die Kostenstellennummer fehlt',
      );

    // TODO remove this logic at some point, we only need this because the import imports student debitorennummern
    // that have not been taken into consideration for the current counter value (see END TODO)
    const currentHighest = (
      await (
        await getRawCollection(Student._collectionName)
      )
        .find({ 'bankInformation.debitorennummer': { $regex: new RegExp(`^${kostenstelle}`) } })
        .project({ 'bankInformation.debitorennummer': 1 })
        .sort({ 'bankInformation.debitorennummer': -1 })
        .limit(1)
        .toArray()
    )[0]?.bankInformation.debitorennummer?.replace(kostenstelle, '');
    const counterValue = +(currentHighest || '0');
    let incrementingNumber =
      await ChancenwerkCounter.getIncrementingStudentDebitorennummerByKostenstellennr(kostenstelle)();
    if (incrementingNumber && counterValue > incrementingNumber) {
      logger.debug(
        { newCounter: counterValue, oldCounter: incrementingNumber },
        'found student with higher debitorennummer, setting counter',
      );
      await (
        await getCollection(ChancenwerkCounter._class)
      ).findOneAndUpdate(
        { type: `${CounterType.DEBITORENNUMMER}_${kostenstelle}` },
        { $set: { counter: counterValue } },
        { upsert: true },
      );
      // refetch based on new starting counter value
      incrementingNumber =
        await ChancenwerkCounter.getIncrementingStudentDebitorennummerByKostenstellennr(kostenstelle)();
    }
    // END TODO

    if (incrementingNumber && incrementingNumber > 999)
      throw new Error('Wir müssen das Format umstellen, drei Nullen reichen nicht mehr');
    student.bankInformation.debitorennummer = kostenstelle + `000${incrementingNumber?.toString()}`.slice(-3);

    await studentColl.updateOne(
      { _id: student._id },
      { $set: { 'bankInformation.debitorennummer': student.bankInformation.debitorennummer } },
    );
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<MongoEntity>,
    newEntity: Student,
    result: UpdateResult,
  ): Promise<void> {
    if (newEntity.has('joinInformation') && newEntity.joinInformation.registermethode === Registermethode.TOKEN) return;
    try {
      await this.ensureDebitorennummer(newEntity);
    } catch (e) {
      logger.error(e);
    }
  }

  private static async findEmailsForRole(kpfID: string, role: string) {
    const predicateCollection = await getRawCollection<ChancenRole>(ChancenRole._collectionName);
    const predicate = await predicateCollection.findOne({ name: role });
    const Taetigkeit = (await import('../../../semantics/Taetigkeit.js')).Taetigkeit;
    // @ts-expect-error
    const relationshipCollection = await getRawCollection<EntityData<Taetigkeit>>(SemanticRelationship._collectionName);
    const relationshipsForKPF = await relationshipCollection
      .find({ objectID: kpfID, predicateID: predicate?._id, ...Taetigkeit.isActiveMongoSelector() })
      .toArray();

    const col = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    const results = await col
      .find({ _id: { $in: relationshipsForKPF.filter(r => r.subjectID).map(r => r.subjectID) } })
      .toArray();

    // Raw mongo docs: users without an address yield undefined, which would later
    // become an empty Recipient and make the whole send fail.
    return results.map(r => r.email).filter(email => !!email && email.length > 0);
  }

  private static async resolveMailReferences(kpfID: string) {
    if (kpfID === '') throw new Error('resolveMailReferences: No kpfID set');

    const kpfColl = await getRawCollection<EntityData<KPF>>(KPF._collectionName);
    const kpf = await kpfColl.findOne({ _id: kpfID }, { projection: { _class: 1, _id: 1, email: 1, produkt: 1 } });

    if (!kpf) throw new Error('Couldnt send registration information! KPF id belongs to a non existen kpf');

    const internalRecipients = await Student.findEmailsForRole(kpfID, Role.INTERNAL_KPF_OPERATIV);

    return { kpf, internalRecipients };
  }

  private static async resolveMailReferencesForInterest(kpfID: string) {
    if (kpfID === '') throw new Error('resolveMailReferences: No kpfID set');

    const kpfColl = await getRawCollection<EntityData<KPF<any>>>(KPF._collectionName);
    const kpf = await kpfColl.findOne(
      { _id: kpfID },
      { projection: { _id: 1, osCoachMail: 1, email: 1, schoolID: 1 } },
    );

    if (!kpf) throw new Error('Couldnt send registration information! KPF id belongs to a non existen kpf');

    const internalRecipients = [kpf.email, kpf.osCoachMail].filter(r => !!r && r.length > 0) as string[];

    return { kpf, internalRecipients };
  }

  @Query(() => Boolean)
  public static sendStudentRegistrationNotification(
    @Arg('student', () => Student) student: Student,
    @Arg('kpfID', () => String) kpfID: string,
    @Arg('schoolName', () => String) schoolName: string,
  ) {
    return async (gqlFields: void) => {
      const { kpf, internalRecipients } = await Student.resolveMailReferences(kpfID);

      try {
        const recipients = [
          ...internalRecipients.map(r => new Recipient(r)),
          new Recipient('anmeldung-verwaltung@chancenwerk.org'),
        ];

        if (kpf?.email) {
          recipients.push(new Recipient(kpf?.email));
        }

        switch (student.schuelertyp) {
          case StudentType.OS:
            {
              const rec = [...recipients, new Recipient('intensivkursdigital@chancenwerk.org')];
              if (kpf?.osCoachMail) {
                rec.push(new Recipient(kpf?.osCoachMail));
              }

              try {
                await chancenWerkMailer.sendMail(
                  await Message.compose({
                    subject: 'Anmeldung eines neuen Jugendlichen',
                    html: registrationInformationOSForAdmin({
                      studentPrename: student.vorname || '',
                      studentLastname: student.nachname || '',
                      studentSchool: schoolName,
                      studentID: student.studentID || 'Kein ID hinterlegt',
                      chosenIntensiveCourses: student.intensiveCoursesChoosen,
                      formOfMembership: student.mitgliedschaft ?? '',
                    }),
                  }),
                  rec,
                );
              } catch (e) {
                logger.error(e, 'Error sending mail');
              }

              try {
                const interestRecipients = await Student.resolveMailReferencesForInterest(kpfID);
                logger.info(interestRecipients.internalRecipients, 'Sending mail to internal recipients');
                await chancenWerkMailer.sendMail(
                  await Message.compose({
                    subject: 'Anmeldung eines neuen Jugendlichen',
                    html: registrationInterestOS({
                      studentPrename: student.vorname || '',
                      studentLastname: student.nachname || '',
                      studentContact: student.email || '',
                      choosenSubjects: [
                        student.preferedFirstSubject,
                        student.preferedSecondSubject,
                        student.preferedThirdSubject,
                        student.preferedOtherSubject,
                      ].filter(s => !!s && s !== '') as string[],
                      formOfMembership: student.mitgliedschaft ?? '',
                    }),
                  }),
                  interestRecipients.internalRecipients.map(r => new Recipient(r)),
                );
              } catch (e) {
                logger.error(e, 'Error sending mail');
              }
            }
            break;
          case StudentType.US:
            await chancenWerkMailer.sendMail(
              await Message.compose({
                subject: 'Anmeldung eines neuen Kindes',
                html: registrationInformationUSForAdmin({
                  studentPrename: student.vorname || '',
                  studentLastname: student.nachname || '',
                  studentSchool: schoolName,
                  studentID: student.studentID || 'Kein ID hinterlegt',
                  studentEmail: student.email ?? student.legalGuardian1.email ?? student.legalGuardian2.email ?? '',
                  studentPhone:
                    student.handynummer ??
                    student.legalGuardian1.handynummer ??
                    student.legalGuardian2.handynummer ??
                    '',
                  studentGrade: student.schoolInformation.jahrgangsstufe?.toString() ?? '',
                }),
              }),
              recipients,
            );
            break;
        }
      } catch (e) {
        console.error(e);
      }
      return true;
    };
  }

  @Mutation(() => Boolean)
  public static sendStudentUpdateDigitalIKNotification(
    @Arg('student', () => Student) student: Student,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return async (gqlFields: void) => {
      const { kpf, internalRecipients } = await Student.resolveMailReferences(kpfID);

      const schoolName = kpf?.school?.name;

      try {
        const recipients = [
          ...internalRecipients.map(r => new Recipient(r)),
          new Recipient('anmeldung-verwaltung@chancenwerk.org'),
        ];

        if (kpf?.email) {
          recipients.push(new Recipient(kpf?.email));
        }

        // only send to OS-students (Oberstufe) for now
        if (student.schuelertyp === StudentType.OS) {
          const rec = [...recipients, new Recipient('intensivkursdigital@chancenwerk.org')];
          if (kpf?.osCoachMail) {
            rec.push(new Recipient(kpf?.osCoachMail));
          }
          await chancenWerkMailer.sendMail(
            await Message.compose({
              subject: 'Neue IK im Chancenraum gewählt',
              html: updateInformationOSForAdmin({
                studentPrename: student.vorname || '',
                studentLastname: student.nachname || '',
                studentSchool: schoolName,
                studentID: student.studentID || 'Kein ID hinterlegt',
                chosenIntensiveCourses: student.intensiveCoursesChoosen,
              }),
            }),
            rec,
          );
        }
      } catch (e) {
        console.error(e);
      }
      return true;
    };
  }

  @Mutation(() => Boolean)
  public static confirmStudentGrade(@Arg('student', () => String) studentID: string) {
    return async (gqlFields: void) => {
      // check permission of calling user to confirm the grade
      const student = await Student.getByID<Student>(studentID)({
        // @ts-expect-error
        schoolInformation: {
          lastChangeOnJahrgangsstufeByUserID: 1,
          lastChangeOnJahrgangsstufeByUser: 1,
          lastChangeOnJahrgangsstufeByUserName: 1,
          lastChangeOnJahrgangsstufe: 1,
          jahrgangsstufe: 1,
        },
        joinInformation: { registermethode: 1 },
        kpfs: {},
      });

      const currentUser = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, contexts: 1 },
        vorname: 1,
        nachname: 1,
        email: 1,
      });

      // not finding the student means the publishfilter already took care of the request
      if (!student || !currentUser) {
        return false;
      }
      const userHasSchukoRightOnStudent = currentUser.roles
        .filter(r => r.name === Role.SCHUKO)
        .flatMap(r => r.contexts)
        .some(context =>
          student.kpfs?.map(kpf => kpf._id).includes(context.replace(new RegExp(`^${ContextPrefix.KPF}`), '')),
        );

      student.schoolInformation.lastChangeOnJahrgangsstufe = DateTime.now();
      student.schoolInformation.lastChangeOnJahrgangsstufeByUserID = currentUser._id;
      student.schoolInformation.lastChangeOnJahrgangsstufeByUserName = currentUser.toString();
      const result = await student.save();

      // search for todos to check
      if (result === 1) {
        const coll = await getRawCollection<EntityData<AdornisTodo>>(AdornisTodo._collectionName);
        for (const kpfOfStudent of student.kpfs ?? []) {
          await coll.updateOne(
            { _id: `schukoGradeReminderGenerator_${currentUser._id}_to_${kpfOfStudent._id}_student_${student._id}` },
            { $set: { status: AdornisTodoStatus.CHECKED, checkedAt: DateTime.now().toJSDate() } },
          );
        }
      }

      return userHasSchukoRightOnStudent;
    };
  }

  @Query(() => Boolean)
  public static confirmRegistration(
    @Arg('studentData', () => Student) studentData: Student,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return async (gqlFields: void) => {
      // ! OS doesn't get a external email
      if (studentData.schuelertyp === StudentType.OS) {
        return;
      }

      const { kpf } = await Student.resolveMailReferences(kpfID);
      const days = (constructValue(kpf).produkt as ProductLKK).durchfuehrungsTage;

      const emailAdress = studentData.legalGuardian1.email
        ? studentData.legalGuardian1.email
        : studentData.legalGuardian2.email;
      try {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Herzlich Willkommen bei Chancenwerk!',
            html: registrationConfirmation(days),
            attachmentPaths: [
              A.absoluteUrl('/api/anmeldungasset/Chancenwerk_I_Informationen_zum_Widerrufsrecht.pdf'),
              A.absoluteUrl(
                'api/student-data/Eingetragene_Daten.pdf',
                new URLSearchParams({
                  studentID: studentData._id,
                  token: await createToken({
                    userID: studentData._id,
                    userClass: this._class,
                    identifier: A.getGloballyUniqueID(),
                    timesToUse: 1,
                    type: TokenType.LOGIN,
                  }),
                }),
              ),
            ],
          }),
          new Recipient(emailAdress),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Query(() => Boolean)
  public static confirmGraduationRegistration(
    @Arg('studentData', () => Student) studentData: Student,
    // @Arg('getPartsPerMail', () => Boolean) getPartsByMail: boolean,
  ) {
    return async (gqlFields: void) => {
      if (studentData.schuelertyp === StudentType.US) {
        return;
      }

      const emailAdress = studentData.email;
      try {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Herzlich Willkommen bei Chancenwerk!',
            html: registrationGraduationConfirmation(),
            attachmentPaths: [],
          }),
          new Recipient(emailAdress),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Query(() => Boolean)
  public static confirmManualUSRegistration(@Arg('studentData', () => Student) studentData: Student) {
    return async (gqlFields: void) => {
      if (studentData.schuelertyp === StudentType.OS) {
        return;
      }

      const kpfs = await StudentParticipationRelationship.getRelationshipsBySubject<StudentParticipationRelationship>(
        studentData._id,
      )({
        objectID: 1,
        object: {
          email: 1,
          schukoTel: 1,
          // @ts-expect-error
          produkt: { durchfuehrungsTage: DurchfuehrungsTag.allFields },
          school: { name: 1 },
          schuKos: { email: 1, handynummer: 1 },
          personsOperativ: { email: 1 },
        },
      });

      if (kpfs.length === 0) {
        throw new Error('Student has no KPFs');
      }

      const emailAdress = studentData.legalGuardian1.email
        ? studentData.legalGuardian1.email
        : studentData.legalGuardian2.email;
      try {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: 'Herzlich Willkommen bei Chancenwerk e.V',
            html: registerUSManualWelcomeMail({
              school: kpfs[0]?.object?.school?.name ?? '',
              days: (kpfs[0]?.object?.produkt as Maybe<ProductLKK>)?.durchfuehrungsTage ?? [],
              schukoEmail: kpfs[0]?.object?.email ?? '',
              schukoHandy: kpfs[0]?.object?.schukoTel ?? '',
            }),
            attachmentPaths: [],
          }),
          new Recipient(
            emailAdress,
            [kpfs[0]?.object?.email ?? '', ...(kpfs[0]?.object?.personsOperativ ?? []).map(p => p.email)].filter(
              email => email.length > 0,
            ),
          ),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Query(() => Student)
  static getStudentWithOneTimeToken(
    @Arg('studentID', () => ID) studentID: string,
    @Arg('token', () => String) token: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Student>) => {
      await verifyToken(token);

      const rawCol = await getRawCollection<Student>(this._collectionName);
      const student = constructValue(await rawCol.findOne({ _id: studentID })) as Student;
      return student;
    };
  }
}

if (A.isServer) {
  void (async () => {
    const coll = await getRawCollection<Student>(Student._collectionName);
    await coll.createIndex({ studentID: 1 });
  })();
}

// Translations for these fields don't seem to work, even when a translation is given for the entity's field. :person_shrugging:
const blacklistedDueToMissingTranslation: BaseQLSelectionSet<Student> = {
  // @ts-expect-error
  contactSourcePerson: {
    roles: 1,
    savedCampusSectionIDs: 1,
    notifyKickOff: 1,
    bankInformation: 1,
    buts: 1,
    kpfs: 1,
    schoolInformation: { _class: 1, lastChangeOnJahrgangsstufeByUserID: 1, lastChangeOnJahrgangsstufeByUser: 1 },
    consent: {
      gotSignedInfopostBack: 1,
      einverstaendnisBild: 1,
      confidential: 1,
      learningDeficiencies: 1,
      einverstaendnisEvaluation: 1,
      einverstaendnisEntbindungVonVerschwiegenheit: 1,
    },
    kuendigung: {
      austrittsdatum: 1,
      confirmationEmailSend: 1,
      confirmationEmailSendAt: 1,
      confirmationEmailProcessFinished: 1,
      kuendigungGrund: 1,
      kuendigungGrundSonst: 1,
      kuendigungArt: 1,
      kuendigungDurch: 1,
    },
    funding: {
      foerderbedarfLautEltern: 1,
      teilleistungsschwaechen: 1,
      teilleistungsschwaechenSonst: 1,
      vermerkeSonst: 1,
    },
    joinInformation: { eintrittsdatum: 1, registermethode: 1, sendedPartTwoTillFive: 1, campusRegisterDataSend: 1 },
    studentID: 1,
    editedByInternal: 1,
    activated: 1,
    registeredViaToken: 1,
    customBusinessAddressEnabled: 1,
    customBusinessAddress: 1,
    businessAddressPartnerID: 1,
    employeeID: 1,
    vertraege: 1,
    intensiveCoursesChoosen: 1,
    lernfoerderungDuerchfuehrungsTagChoosen: { cid: 1 },
    businessAddress: { partnerID: 1 },
    unterlagen: {
      vertragVersendet: 1,
      vertragErhalten: 1,
      zusatzvereinbarungVersendet: 1,
      zusatzvereinbarungErhalten: 1,
      selbstverplichtungserklaerungErhalten: 1,
      datenschutzerklaerungErhalten: 1,
      fuehrungzeugnisGueltigAb: 1,
      fuehrungzeugnisEinsichtnahmeErfolgt: 1,
      fuehrungzeugnisRelevanteEintraege: 1,
      vereinbarungUrheberrechtErhalten: 1,
      byodFormularErhalten: 1,
      ersthelferkursAbsolviertAm: 1,
      aktuelleMasernimpfungNachgewiesen: 1,
      einwilligungserklaerungMasernimpfungNachweisErhalten: 1,
      personalNummer: 1,
      kreditorenNummer: 1,
      kommunikationMitUL: 1,
      sonstigeVermerke: 1,
    },
    kontoInformationen: { kontoinhaber: 1, iban: 1, bic: 1, kreditinstitut: 1 },
    onboarding: {
      einarbeitungAbsolviertAm: 1,
      onboardingAkademieAbsolviertAm: 1,
      feedbackquizCompletedAt: 1,
      feedbacktalkCompletedAt: 1,
      trainingFinishedAt: 1,
    },
    workshopsAndTrainingInformation: {
      participatedOSWorkshopOneRoleModel: 1,
      carriedOutOSWorkshopOneRoleModel: 1,
      participatedOSWorkshopTwoChallenges: 1,
      carriedOutOSWorkshopTwoChallenges: 1,
      participatedSuSWorkshopLearningToLearn: 1,
      carriedOutSuSWorkshopLearningToLearn: 1,
      participatedSuSWorkshopMentalStrength: 1,
      carriedOutSuSWorkshopMentalStrength: 1,
      participatedSusWorkshopInnerStrenght: 1,
      carriedOutSusWorkshopInnerStrenght: 1,
      participatedULTrainingOSCompanion: 1,
      participatedULWorkshopCompentenceOne: 1,
      participatedULWorkshopCompentenceTwo: 1,
      participatedULWorkshopMotivation: 1,
      participatedULWorkshopToDos: 1,
      participatedULWorkshopStress: 1,
      participatedULWorkshopCommunication: 1,
      participatedULWorkshopStorytelling: 1,
      participatedULWorkshopLanguage: 1,
      participatedULWorkshopDidacticReduction: 1,
      participatedULHeftCampusKurve: 1,
    },
    workAvailabilities: { weekday: 1, from: 1, to: 1 },
  },
  // @ts-expect-error
  personalSignOnChrstimasCardFrom: {
    roles: 1,
    contactSourcePerson: 1,
    personalSignOnChrstimasCardFrom: 1,
    savedCampusSectionIDs: 1,
    tags: 1,
    notifyKickOff: 1,
    bankInformation: 1,
    buts: 1,
    kpfs: 1,
    schoolInformation: { _class: 1, lastChangeOnJahrgangsstufeByUserID: 1, lastChangeOnJahrgangsstufeByUser: 1 },
    consent: {
      gotSignedInfopostBack: 1,
      einverstaendnisBild: 1,
      confidential: 1,
      learningDeficiencies: 1,
      einverstaendnisEvaluation: 1,
      einverstaendnisEntbindungVonVerschwiegenheit: 1,
    },
    kuendigung: {
      austrittsdatum: 1,
      confirmationEmailSend: 1,
      confirmationEmailSendAt: 1,
      confirmationEmailProcessFinished: 1,
      kuendigungGrund: 1,
      kuendigungGrundSonst: 1,
      kuendigungArt: 1,
      kuendigungDurch: 1,
    },
    funding: {
      foerderbedarfLautEltern: 1,
      teilleistungsschwaechen: 1,
      teilleistungsschwaechenSonst: 1,
      vermerkeSonst: 1,
    },
    joinInformation: { eintrittsdatum: 1, registermethode: 1, sendedPartTwoTillFive: 1, campusRegisterDataSend: 1 },
    studentID: 1,
    editedByInternal: 1,
    activated: 1,
    registeredViaToken: 1,
    customBusinessAddressEnabled: 1,
    customBusinessAddress: 1,
    businessAddressPartnerID: 1,
    employeeID: 1,
    vertraege: 1,
    intensiveCoursesChoosen: 1,
    lernfoerderungDuerchfuehrungsTagChoosen: { cid: 1 },
    businessAddress: { partnerID: 1 },
    unterlagen: {
      vertragVersendet: 1,
      vertragErhalten: 1,
      zusatzvereinbarungVersendet: 1,
      zusatzvereinbarungErhalten: 1,
      selbstverplichtungserklaerungErhalten: 1,
      datenschutzerklaerungErhalten: 1,
      fuehrungzeugnisGueltigAb: 1,
      fuehrungzeugnisEinsichtnahmeErfolgt: 1,
      fuehrungzeugnisRelevanteEintraege: 1,
      vereinbarungUrheberrechtErhalten: 1,
      byodFormularErhalten: 1,
      ersthelferkursAbsolviertAm: 1,
      aktuelleMasernimpfungNachgewiesen: 1,
      einwilligungserklaerungMasernimpfungNachweisErhalten: 1,
      personalNummer: 1,
      kreditorenNummer: 1,
      kommunikationMitUL: 1,
      sonstigeVermerke: 1,
    },
    kontoInformationen: { kontoinhaber: 1, iban: 1, bic: 1, kreditinstitut: 1 },
    onboarding: {
      einarbeitungAbsolviertAm: 1,
      onboardingAkademieAbsolviertAm: 1,
      feedbackquizCompletedAt: 1,
      feedbacktalkCompletedAt: 1,
      trainingFinishedAt: 1,
    },
    workshopsAndTrainingInformation: {
      participatedOSWorkshopOneRoleModel: 1,
      carriedOutOSWorkshopOneRoleModel: 1,
      participatedOSWorkshopTwoChallenges: 1,
      carriedOutOSWorkshopTwoChallenges: 1,
      participatedSuSWorkshopLearningToLearn: 1,
      carriedOutSuSWorkshopLearningToLearn: 1,
      participatedSuSWorkshopMentalStrength: 1,
      carriedOutSuSWorkshopMentalStrength: 1,
      participatedSusWorkshopInnerStrenght: 1,
      carriedOutSusWorkshopInnerStrenght: 1,
      participatedULTrainingOSCompanion: 1,
      participatedULWorkshopCompentenceOne: 1,
      participatedULWorkshopCompentenceTwo: 1,
      participatedULWorkshopMotivation: 1,
      participatedULWorkshopToDos: 1,
      participatedULWorkshopStress: 1,
      participatedULWorkshopCommunication: 1,
      participatedULWorkshopStorytelling: 1,
      participatedULWorkshopLanguage: 1,
      participatedULWorkshopDidacticReduction: 1,
      participatedULHeftCampusKurve: 1,
    },
    workAvailabilities: { weekday: 1, from: 1, to: 1 },
  },
};

export const STUDENT_BLACKLIST: BaseQLSelectionSet<Student> = {
  ...blacklistedDueToMissingTranslation,
  flags: { _class: 1, getAll: 1 },
  // @ts-expect-error
  createdByPerson: 1,
  createdBy: 1,
  studentRelationships: 1,
  // @ts-expect-error
  contactSourcePerson: PERSON_BLACKLIST,
  // @ts-expect-error
  roles: 1,
  studentID: 1,
  employeeID: 1,
  emailConfirmed: 1,
  ios_sub: 1,
  lastActivity: 1,
  contactSourcePersonID: 1,
  savedCampusSectionIDs: 1,
  customBusinessAddressEnabled: 1,
  businessAddressPartnerID: 1,
  personalSignOnChrstimasCardFromID: 1,
  personalSignOnChrstimasCardFromIDs: 1,
  // @ts-expect-error
  personalSignOnChrstimasCardFrom: PERSON_BLACKLIST,
  bankInformation: { _class: 1 },
  adresse: { _class: 1, zipCity: { _class: 1 } },
  notifyKickOff: 1,
  legalGuardian1: { _class: 1, adresse: { _class: 1, zipCity: { _class: 1, zip: 1, city: 1 } } },
  legalGuardian2: { _class: 1, adresse: { _class: 1, zipCity: { _class: 1, zip: 1, city: 1 } } },
  // @ts-expect-error
  schoolInformation: {
    _class: 1,
    lastChangeOnJahrgangsstufeByUserID: 1,
    lastChangeOnJahrgangsstufeByUser: 1,
    aweichendeSchule: 1,
    abweichendeSchuleID: 1,
    alternativeContactPerson: PERSON_BLACKLIST,
    contactSourcePerson: PERSON_BLACKLIST,
    abweichendeSchuleResolved: 1,
    // abweichendeSchuleResolved: {
    //   contactPerson: PERSON_BLACKLIST,
    //   contactSourcePerson: PERSON_BLACKLIST,
    //   personalSignOnChrstimasCardFrom: PERSON_BLACKLIST,
    //   endedTroughPerson: PERSON_BLACKLIST,
    //   personDidAcquisitionMeeting: PERSON_BLACKLIST,
    //   handoverEmailWrittenBy: PERSON_BLACKLIST,
    //   acquisitionMultiplicatorPerson: PERSON_BLACKLIST,
    // },
  },
  buts: { _class: 1, faecher: { _id: 1, _class: 1 } },
  registeredViaToken: 1,
  customBusinessAddress: 1,
  activated: 1,
  // @ts-expect-error
  intensiveCoursesChoosen: 1,
  lernfoerderungDuerchfuehrungsTagChoosen: {
    _class: 1,
    wochentag: 1,
    startZeit: 1,
    endZeit: 1,
    locations: 1,
    cid: 1, // Translation not working for this field, so we blacklist it for now.
  },
  editedByInternal: 1,
  vertraege: 1,
  type: 1,
  graduationInformation: { _class: 1 },
  partnerRelations: 1,
  // @ts-expect-error
  levumiResults: 1,
  // @ts-expect-error
  lernhefteWorkingOn: 1,
  // @ts-expect-error
  lernhefteFinished: 1,
  lernhefteFinishedIDs: 1,
  lernhefteWorkingOnIDs: 1,
  examPreperationChoosen: 1,
  // @ts-expect-error
  intensiveCoursesSingleChoosen: 1,
  // @ts-expect-error
  kpfs: {
    produkt: PRODUCT_BLACKLIST,
    school: PARTNER_BLACKLIST,
    acquisitionRecord: 1,
    ansprechPersonExtern: PERSON_BLACKLIST,
    butAnsprechPersonExtern: PERSON_BLACKLIST,
    personsStrategisch: PERSON_BLACKLIST,
    personsOperativ: PERSON_BLACKLIST,
    personsBUTOperativ: PERSON_BLACKLIST,
    personsVerwaltung: PERSON_BLACKLIST,
    schoolSupport: PERSON_BLACKLIST,
    schuKos: 1,
    leKos: 1,
    usStudents: 1,
    osStudents: 1,
    ragPartnerInformation: 1,
    substituteKPFs: 1,
  },
};

export const updateAndConfirmGrade = registerMutation({
  type: () => Boolean,
  operationName: 'updateAndConfirmGrade',
  options: {},
  params: [
    { name: 'studentID', type: () => ID },
    { name: 'newValue', type: () => Int },
  ],
  resolve: (studentID: string, newValue: number) => async (gqlFields: void) => {
    const student = await getByID<typeof Student>(Student, studentID)({ schoolInformation: { jahrgangsstufe: 1 } });
    if (!student) {
      logger.debug({ studentID }, 'could not find student by id');
      return false;
    }
    if (!student.schoolInformation) {
      student.schoolInformation = new SchoolInformation({ jahrgangsstufe: newValue });
    }
    if (student.schoolInformation && student.schoolInformation.jahrgangsstufe !== newValue) {
      student.schoolInformation.jahrgangsstufe = newValue;
    }
    try {
      const sr = await student.save(); // ! ignoring result
      logger.debug({ saveResult: sr, studentID }, 'Saved student');
      return await Student.confirmStudentGrade(studentID)();
    } catch (err) {
      logger.error({ err, studentID, newValue }, 'Error updating grade');
      return false;
    }
  },
});

// * Formatting functions
export const formatDaysAvailable = (daysAvailable: DayAvailable[]): string => {
  //sort by weekday
  const sortedDays = daysAvailable.sort((a, b) => {
    const dayOrder = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
  });
  return sortedDays.map(d => `${d.day} (${formatTimesAvailable(d)})`).join(' · ');
};

export const formatDayAvailable = (day: DayAvailable): string => {
  const formattedString = `${day.day} (${formatTimesAvailable(day)})`;
  return formattedString;
};

export const formatTimesAvailable = (day: DayAvailable): string => {
  if (!day.timesAvailable || day.timesAvailable.length === 0) {
    return 'k.A.';
  }
  return day.timesAvailable.map(t => t.toFormat('HH:mm')).join(', ');
};

export const formatCrashkurse = (courses: DurchfuehrungsTag[]): string => {
  return courses
    .map(c => c.info)
    .map(c => (c && c.length > 50 ? `${c.slice(0, 50)}…` : c))
    .join(' · ');
};

export const formatExamPreparationCourses = (courses: ExamPreparationCopy[]): string => {
  return courses
    .map(c => `${c.subject} - ${c.weekday} ${c.startTime?.toFormat('HH:mm')}-${c.endTime?.toFormat('HH:mm')}`)
    .join(', ');
};
